import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { applyMask, maskChange, maskFor } from "../docs/mask";
import { phpDate, ru } from "../docs/helpers";
import type { Meta, Patient, SessionUser, Temp } from "../types";
import { useMessage } from "./Message";

type Values = Record<string, string>;

/**
 * Поля формы, у которых есть «варианты»: сохранённые значения, из которых поле
 * заполняется одним кликом. У полей, что и раньше имели сниппеты, id прежние
 * (общие с документами — bplace, doc_org, doctor…); остальные получили свои,
 * с префиксом pat_. Даты вариантов не имеют: дата — всегда своя.
 *
 * Варианты живут в той же личной библиотеке temps, что и сниппеты документов:
 * «+» у поля сохраняет введённое, а при наборе совпадающие варианты
 * поднимаются в подсказке наверх.
 */
const OPTION_FIELDS: Record<string, string> = {
  fio: "pat_fio",
  bplace: "bplace",
  phone: "pat_phone",
  doc_seria: "pat_doc_seria",
  doc_number: "pat_doc_number",
  doc_org: "doc_org",
  doc_code: "pat_doc_code",
  policy_seria: "pat_policy_seria",
  policy_number: "pat_policy_number",
  policy_org: "policy_org",
  citizen: "citizen",
  snils: "pat_snils",
  email: "pat_email",
  town: "town",
  address: "pat_address",
  address_now: "pat_address_now",
  employment: "employment",
  disabled: "disabled",
  job: "job",
  third: "pat_third",
  third_phone: "pat_third_phone",
  trustee: "pat_trustee",
  doctor: "doctor",
  anes: "anes",
  comment: "pat_comment",
};

/**
 * Port of template-parts/pat.php — one form used for both adding and editing,
 * with the same three-stage guard the legacy jQuery had:
 *   1. required fields must be present   (ajax/pat_check.php)
 *   2. a same-name/same-DOB record warns (ajax/pat_check_copy.php)
 *   3. any other blank field warns once, and a second click submits
 */
export default function PatientForm({
  patient,
  meta,
  user = null,
  onClose,
  onSaved,
}: {
  patient: Patient | null;
  meta: Meta;
  /** Право «+» у полей: варианты добавляет тот, кому можно добавлять шаблоны. */
  user?: SessionUser | null;
  onClose: () => void;
  onSaved: (saved: Patient | null) => void;
}) {
  const isEdit = patient !== null;
  const message = useMessage();
  const canAddOpt = !!(user?.can_add_temp || user?.is_admin);
  /** Варианты общие для всех (019): удаление убирает у всех — только с правом. */
  const canDelOpt = !!(user?.can_del_temp || user?.is_admin);

  const initial = useMemo<Values>(() => {
    const d = (v: string | null | undefined) => (v ? ru(v) : "");
    return {
      created_at: patient ? d(patient.created_at) : phpDate("d.m.Y"),
      fio: patient?.fio ?? "",
      bdate: d(patient?.bdate),
      bplace: patient?.bplace ?? "",
      phone: patient?.phone ?? "",
      sex: patient?.sex ?? "1",
      source: patient?.source ? String(patient.source) : "1",
      doc_type: patient?.doc_type ? String(patient.doc_type) : "1",
      doc_seria: patient?.doc_seria ?? "",
      doc_number: patient?.doc_number ?? "",
      doc_org: patient?.doc_org ?? "",
      doc_code: patient?.doc_code ?? "",
      doc_date: d(patient?.doc_date),
      policy_seria: patient?.policy_seria ?? "",
      policy_number: patient?.policy_number ?? "",
      policy_org: patient?.policy_org ?? "",
      policy_date: d(patient?.policy_date),
      citizen: patient?.citizen ?? "",
      snils: patient?.snils ?? "",
      email: patient?.email ?? "",
      town: patient?.town ?? "",
      address: patient?.address ?? "",
      address_date: d(patient?.address_date),
      address_now: patient?.address_now ?? "",
      employment: patient?.employment ?? "",
      disabled: patient?.disabled ?? "",
      job: patient?.job ?? "",
      third: patient?.third ?? "",
      third_phone: patient?.third_phone ?? "",
      trustee: patient?.trustee ?? "",
      doctor: patient?.doctor ?? "",
      anes: patient?.anes ?? "",
      comment: patient?.comment ?? "",
    };
  }, [patient]);

  const [values, setValues] = useState<Values>(initial);
  const [warnIncomplete, setWarnIncomplete] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  /** Похожие карточки (фамилия + дата рождения) — кто это может быть. */
  const [dupList, setDupList] = useState<{ pat_id: number; fio: string; bdate: string | null; phone: string }[]>([]);
  /** Незаполненные обязательные поля — подсвечены, пока их не поправят. */
  const [invalid, setInvalid] = useState<Set<string>>(new Set());
  const [forced, setForced] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  /* --- варианты полей: личная библиотека, одним запросом на всю форму --- */
  const [opts, setOpts] = useState<Record<string, Temp[]>>({});
  /** Поле с открытой подсказкой; закрывается по blur (клики по строкам —
      mousedown, чтобы успеть раньше). */
  const [sugFor, setSugFor] = useState<string | null>(null);

  useEffect(() => {
    api
      .tempsBulk([...new Set(Object.values(OPTION_FIELDS))])
      .then((r) => setOpts(r.temps))
      .catch(() => setOpts({}));
  }, []);

  const set = (name: string, value: string) => {
    setValues((p) => ({ ...p, [name]: value }));
    // Поправили поле — подсветка «не заполнено» с него снимается.
    setInvalid((prev) => {
      if (!prev.has(name)) return prev;
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  /** «+» у поля: сохранить введённое как вариант этого поля. */
  async function addOption(name: string) {
    const fieldId = OPTION_FIELDS[name];
    const value = (values[name] ?? "").trim();
    if (!fieldId || !value) {
      message("Сначала введите значение — оно и станет вариантом", "error");
      return;
    }
    const existing = opts[fieldId] ?? [];
    if (existing.some((t) => t.text.trim().toLowerCase() === value.toLowerCase())) {
      message("Такой вариант у этого поля уже есть", "error");
      return;
    }
    try {
      const created = await api.createTemp(fieldId, value.slice(0, 100), value);
      setOpts((p) => ({ ...p, [fieldId]: [...(p[fieldId] ?? []), created] }));
      message("Вариант сохранён — теперь он в подсказке этого поля");
    } catch (err) {
      message(err as Error);
    }
  }

  /** «×» у варианта: убрать его из личной библиотеки этого поля. */
  async function delOption(fieldId: string, t: Temp) {
    try {
      await api.deleteTemp(t.temp_id);
      setOpts((p) => ({ ...p, [fieldId]: (p[fieldId] ?? []).filter((x) => x.temp_id !== t.temp_id) }));
      message("Вариант удалён");
    } catch (err) {
      message(err as Error);
    }
  }

  /** Подсказка поля: совпадающие с введённым варианты — наверху. */
  const renderOptions = (name: string, mask: string | null) => {
    const fieldId = OPTION_FIELDS[name];
    if (!fieldId || sugFor !== name) return null;
    const list = opts[fieldId] ?? [];
    if (!list.length) return null;
    const q = (values[name] ?? "").trim().toLowerCase();
    const hits = q ? list.filter((t) => t.text.toLowerCase().includes(q)) : [];
    const rest = q ? list.filter((t) => !t.text.toLowerCase().includes(q)) : list;
    const pick = (text: string) => {
      set(name, mask ? applyMask(mask, text) : text);
      setSugFor(null);
    };
    return (
      <div className="opt-drop scroll">
        {[...hits.map((t) => ({ t, hit: true })), ...rest.map((t) => ({ t, hit: false }))].map(
          ({ t, hit }) => (
            <div
              key={t.temp_id}
              className={`opt-row${hit ? " opt-hit" : ""}`}
              // mousedown: клик должен успеть раньше, чем blur закроет список.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(t.text);
              }}
            >
              <span className="opt-row-text">{t.text}</span>
              {/* Учётка сотрудника — из «Пользователей», здесь не удаляется. */}
              {canDelOpt && !t.account && (
                <i
                  className="opt-del"
                  title="Удалить этот вариант"
                  onMouseDown={(e) => {
                    // Свой mousedown: не дать строке подставить текст и blur — закрыть список.
                    e.preventDefault();
                    e.stopPropagation();
                    void delOption(fieldId, t);
                  }}
                >
                  ×
                </i>
              )}
            </div>
          )
        )}
      </div>
    );
  };

  const REQUIRED = ["created_at", "fio", "bdate", "bplace", "phone"];
  const REQUIRED_LABEL: Record<string, string> = {
    created_at: "дата",
    fio: "Ф.И.О.",
    bdate: "дата рождения",
    bplace: "место рождения",
    phone: "телефон",
  };

  async function submit() {
    if (busy) return;
    setError("");

    const missing = REQUIRED.filter((k) => !values[k]?.trim());
    if (missing.length || values.source === "1") {
      // Называем именно то, чего не хватает, а не весь список обязательных.
      const names = missing.map((k) => REQUIRED_LABEL[k]);
      if (values.source === "1") names.push("канал привлечения");
      setInvalid(new Set([...missing, ...(values.source === "1" ? ["source"] : [])]));
      const text = `Заполните: ${names.join(", ")}.`;
      setError(text);
      message(text, "error");
      return;
    }
    setInvalid(new Set());

    if (!forced) {
      // Похожие карточки: та же фамилия и дата рождения (или то же Ф.И.О.).
      try {
        const dup = await api.checkDuplicate(values.fio.trim(), values.bdate, patient?.pat_id);
        if (dup.duplicate) {
          setDupList(dup.candidates);
          setDuplicate(true);
          return;
        }
      } catch (err) {
        message(err as Error);
        return;
      }
    }

    const blanks = Object.entries(values).filter(([k, v]) => k !== "trustee" && !String(v).trim());
    if (blanks.length && !warnIncomplete && !forced) {
      setWarnIncomplete(true);
      return;
    }

    setBusy(true);
    try {
      if (isEdit) {
        await api.updatePatient(patient!.pat_id, values);
        const { patient: fresh } = await api.patient(patient!.pat_id);
        onSaved(fresh);
      } else {
        const { pat_id } = await api.createPatient(values);
        const { patient: fresh } = await api.patient(pat_id);
        onSaved(fresh);
      }
    } catch (err) {
      setError((err as Error).message);
      message(err as Error);
    } finally {
      setBusy(false);
    }
  }

  // Renders one labelled input. A plain render helper, deliberately NOT a
  // nested component: a component type recreated on every render makes React
  // remount each field per keystroke — dropping focus and detaching the
  // snippet popup from its target element.
  const renderInput = ({
    name,
    label,
    className,
    type = "text",
    placeholder,
    wrapClass = "input",
  }: {
    name: string;
    label: string;
    /** Оставлен для совместимости вызовов; варианты определяет OPTION_FIELDS. */
    tpl?: string;
    className?: string;
    type?: string;
    placeholder?: string;
    wrapClass?: string;
  }) => {
    const mask = maskFor(className);
    const optable = !!OPTION_FIELDS[name];
    return (
      <div className={`${wrapClass}${optable ? " opt-field" : ""}`} key={name}>
        <div>{label}</div>
        <input
          type={type}
          name={name}
          className={[className, invalid.has(name) ? "pat-input-bad" : null].filter(Boolean).join(" ") || undefined}
          placeholder={placeholder}
          autoComplete="off"
          value={values[name] ?? ""}
          onFocus={() => optable && setSugFor(name)}
          // Выбор закрыл список, фокус остался в поле — клик или набор открывают снова.
          onClick={() => optable && setSugFor(name)}
          onBlur={() => optable && setSugFor((v) => (v === name ? null : v))}
          onChange={(e) => {
            set(name, mask ? maskChange(e, mask, values[name] ?? "") : e.target.value);
            if (optable) setSugFor(name);
          }}
        />
        {optable && canAddOpt && (
          <i
            className="opt-add"
            title="Сохранить введённое как вариант этого поля"
            // mousedown с preventDefault — фокус остаётся в поле.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => addOption(name)}
          >
            +
          </i>
        )}
        {optable && renderOptions(name, mask)}
      </div>
    );
  };

  return (
    <div className="overlay" style={{ opacity: 1 }}>
      <div className="overlay-frame">
        <div className="close" onClick={onClose} />
        <div className="overlay-content scroll">
          <div className="pat-add">
            {duplicate && (
              <div className="pat-add-warn" style={{ display: "block" }}>
                <div className="pat-add-warn__content pat-dup">
                  <div className="pat-add-warn__title">Похоже, такой пациент уже есть</div>
                  <div className="pat-add-warn__text">
                    Совпадает фамилия и дата рождения. Чтобы не завести вторую карточку, откройте
                    существующую{isEdit ? " или объедините карточки — всё из этой переедет в выбранную" : ""}.
                  </div>
                  <div className="pat-dup-list">
                    {dupList.map((d) => (
                      <div className="pat-dup-row" key={d.pat_id}>
                        <div className="pat-dup-who">
                          <b>{d.fio}</b>
                          <span>
                            {d.bdate ? ru(d.bdate) : "дата рождения не указана"}
                            {d.phone ? ` · ${d.phone}` : ""}
                          </span>
                        </div>
                        {!isEdit && (
                          <div
                            className="button"
                            onClick={async () => {
                              try {
                                const { patient: existing } = await api.patient(d.pat_id);
                                onSaved(existing);
                              } catch (err) {
                                message(err as Error);
                              }
                            }}
                          >
                            Открыть эту карту
                          </div>
                        )}
                        {isEdit && (user?.can_delete || user?.is_admin) && (
                          <div
                            className="button pat-dup-merge"
                            title="Случаи, документы, записи в списках и на приём этой карточки переедут в выбранную; эта карточка будет удалена"
                            onClick={async () => {
                              if (busy) return;
                              setBusy(true);
                              try {
                                const r = await api.mergePatient(patient!.pat_id, d.pat_id);
                                message(
                                  `Карточки объединены: перенесено случаев ${r.moved.cases}, записей ${r.moved.entries}, приёмов ${r.moved.appts}`
                                );
                                const { patient: fresh } = await api.patient(d.pat_id);
                                onSaved(fresh);
                              } catch (err) {
                                message(err as Error);
                              } finally {
                                setBusy(false);
                              }
                            }}
                          >
                            Объединить с этой
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="pat-dup-actions">
                    <div
                      className="button pat-add-warn__button"
                      onClick={() => {
                        setDuplicate(false);
                        setForced(true);
                      }}
                    >
                      {isEdit ? "Это другой человек — сохранить" : "Это другой человек — создать"}
                    </div>
                    <u className="hist-link" onClick={() => setDuplicate(false)}>
                      назад к форме
                    </u>
                  </div>
                </div>
              </div>
            )}

            <form id="pat-add" onSubmit={(e) => e.preventDefault()}>
              {renderInput({ name: "created_at", label: "Дата добавления", className: "mask-date", placeholder: "Пример: 05.02.1961" })}

              <div className="pat-add-row half high">
                {renderInput({ name: "fio", label: "Ф.И.О.", wrapClass: "input input-third" })}
                {renderInput({ name: "bdate", label: "Дата рождения", type: "tel", className: "mask-date", placeholder: "Пример: 05.02.1961", wrapClass: "input input-third" })}
                {renderInput({ name: "bplace", label: "Место рождения", tpl: "bplace", placeholder: "Пример: Республика Адыгея, г. Майкоп", wrapClass: "input input-third" })}
                {renderInput({ name: "phone", label: "Номер телефона", type: "tel", className: "mask-phone", wrapClass: "input input-third" })}
                <div className="input input-third">
                  <div>Пол</div>
                  <select name="sex" value={values.sex} onChange={(e) => set("sex", e.target.value)}>
                    {Object.entries(meta.sex).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="input input-third">
                  <div>Канал привлечения</div>
                  <select
                    name="source"
                    value={values.source}
                    onChange={(e) => set("source", e.target.value)}
                  >
                    {Object.entries(meta.sources).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="pat-add-docs">
                <strong>Документ:</strong>
                {Object.entries(meta.docTypes).map(([k, v]) => (
                  <div
                    key={k}
                    className={values.doc_type === k ? "selected" : undefined}
                    onClick={() => set("doc_type", k)}
                  >
                    {v}
                  </div>
                ))}
              </div>

              <div className="pat-add-row">
                {renderInput({ name: "doc_seria", label: "Серия документа", type: "tel", wrapClass: "input input-third" })}
                {renderInput({ name: "doc_number", label: "Номер документа", type: "tel", wrapClass: "input input-third" })}
                {renderInput({ name: "doc_org", label: "Кем выдан", tpl: "doc_org", wrapClass: "input input-third" })}
              </div>

              <div className="pat-add-row half">
                {renderInput({ name: "doc_code", label: "Код подразделения", type: "tel", className: "mask-code" })}
                {renderInput({ name: "doc_date", label: "Дата выдачи", type: "tel", className: "mask-date", placeholder: "Пример: 07.04.1972" })}
              </div>

              <div className="pat-add-row quarter">
                {renderInput({ name: "policy_seria", label: "Серия полиса", type: "tel" })}
                {renderInput({ name: "policy_number", label: "Номер полиса", type: "tel" })}
                {renderInput({ name: "policy_org", label: "Страховая организация", tpl: "policy_org" })}
                {renderInput({ name: "policy_date", label: "Дата выдачи", type: "tel", className: "mask-date", placeholder: "Пример: 09.08.1984" })}
              </div>

              <div className="pat-add-row">
                {renderInput({ name: "citizen", label: "Гражданство", tpl: "citizen" })}
                {renderInput({ name: "snils", label: "СНИЛС", type: "tel", className: "mask-snils" })}
                {renderInput({ name: "email", label: "Электронная почта" })}
              </div>

              <div className="pat-add-row third">
                {renderInput({ name: "town", label: "Город (населенный пункт)", tpl: "town" })}
                {renderInput({ name: "address", label: "Адрес регистрации (прописка)" })}
                {renderInput({ name: "address_date", label: "Дата регистрации прописки", type: "tel", className: "mask-date", placeholder: "Пример: 15.05.2000", wrapClass: "input input-third" })}
              </div>

              <div className="pat-add-row third">
                <div className="input opt-field opt-field-paste">
                  <div>Адрес фактического проживания</div>
                  <input
                    type="text"
                    name="address_now"
                    style={{ paddingRight: 70 }}
                    autoComplete="off"
                    value={values.address_now}
                    onFocus={() => setSugFor("address_now")}
                    onClick={() => setSugFor("address_now")}
                    onBlur={() => setSugFor((v) => (v === "address_now" ? null : v))}
                    onChange={(e) => {
                      set("address_now", e.target.value);
                      setSugFor("address_now");
                    }}
                  />
                  <div
                    className="input__paste input__paste--now"
                    title="Скопировать из прописки"
                    onClick={() =>
                      set(
                        "address_now",
                        [values.town, values.address].filter(Boolean).join(", ")
                      )
                    }
                  />
                  {canAddOpt && (
                    <i
                      className="opt-add"
                      title="Сохранить введённое как вариант этого поля"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => addOption("address_now")}
                    >
                      +
                    </i>
                  )}
                  {renderOptions("address_now", null)}
                </div>
                {renderInput({ name: "employment", label: "Занятость", tpl: "employment" })}
                {renderInput({ name: "disabled", label: "Группа инвалидности", tpl: "disabled" })}
              </div>

              <div className="pat-add-row third">
                {renderInput({ name: "job", label: "Место работы", tpl: "job" })}
                {renderInput({ name: "third", label: "Третье лицо для передачи информации" })}
                {renderInput({ name: "third_phone", label: "Телефон третьего лица", type: "tel", className: "mask-phone" })}
              </div>

              <div className="pat-add-row third">
                {renderInput({ name: "trustee", label: "Опекун/представитель" })}
                {renderInput({ name: "doctor", label: "Лечащий врач", tpl: "doctor" })}
                {renderInput({ name: "anes", label: "Анестезиолог:", tpl: "anes" })}
              </div>

              {renderInput({ name: "comment", label: "Комментарий" })}

              <div className="pat-add-submit">
                {error && <div className="pat-add-warning" style={{ display: "block" }}>{error}</div>}
                {warnIncomplete && !error && (
                  <div className="pat-add-warning" style={{ display: "block" }}>
                    Внимание! Заполнены не все поля. Если хотите продолжить, нажмите кнопку «
                    {isEdit ? "Сохранить" : "Добавить"}» еще раз.
                  </div>
                )}
                <div
                  className={`button ${isEdit ? "pat-edit-action" : "pat-add-action"}`}
                  onClick={submit}
                >
                  {busy ? "…" : isEdit ? "Сохранить изменения" : "Добавить"}
                  <div />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

    </div>
  );
}
